import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Header, Footer } from "../components/layout";
import { Container } from "../components/ui/Container";
import { AppDownloadBanner } from "../components/sections";
import { SEO } from "../components/SEO";
import { getOfferDetails } from "../apis/offers";
import type { DetailedOffer } from "../apis/offers";
import { APP_STORE_URL, PLAY_STORE_URL } from "../utils/appLinks";

function toSafeString(val: any, fallback = ""): string {
  if (val == null) return fallback;
  if (typeof val === "string") return val.trim() || fallback;
  if (typeof val === "number") return String(val);
  if (typeof val === "object") {
    return val.name || val.title || val.text || val.term || val.label || fallback;
  }
  return String(val) || fallback;
}

export default function OfferDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [offer, setOffer] = useState<DetailedOffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    if (!id) {
      setError("Offer not found");
      setLoading(false);
      return;
    }

    getOfferDetails(id)
      .then((data) => {
        if (!cancelled) setOffer(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Offer not found");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F7FA] text-gray-900">
        <Header />
        <Container size="xl" padding="md" className="pt-24 pb-16">
          <div className="h-6 w-64 bg-gray-200 animate-pulse rounded-md mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
            <div className="lg:col-span-7 h-96 bg-gray-200 animate-pulse rounded-3xl" />
            <div className="lg:col-span-5 space-y-4">
              <div className="h-56 bg-gray-200 animate-pulse rounded-3xl" />
              <div className="h-36 bg-gray-200 animate-pulse rounded-3xl" />
            </div>
          </div>
        </Container>
        <Footer />
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="min-h-screen bg-[#F6F7FA] text-gray-900">
        <Header />
        <Container size="xl" padding="md" className="pt-32 pb-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 text-red-600 flex items-center justify-center text-2xl font-bold">
            !
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
            Offer Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            The deal you are looking for has expired or is no longer available.
          </p>
          <Link
            to="/deals"
            className="px-6 py-2.5 rounded-full bg-[#18123A] text-white font-medium hover:bg-[#251E5C] transition-colors inline-block"
          >
            Explore all deals
          </Link>
        </Container>
        <Footer />
      </div>
    );
  }

  const partner = offer.partnerId;
  const merchantName = toSafeString(partner?.businessDetails?.businessName, "Partner Store");
  const merchantSlug = partner?.slug || "";
  const rawAddress = partner?.businessDetails?.address;
  const merchantAddress =
    typeof rawAddress === "string"
      ? rawAddress
      : typeof rawAddress === "object" && rawAddress
      ? [
          toSafeString((rawAddress as any).line1),
          toSafeString((rawAddress as any).city),
          toSafeString((rawAddress as any).district),
        ]
          .filter(Boolean)
          .join(", ")
      : toSafeString(partner?.businessDetails?.district, "");
  const merchantLogo = partner?.businessInfo?.businessLogo || "";
  const distance = toSafeString(offer.distance, "");

  const coverImage =
    offer.images && offer.images.length > 0
      ? offer.images[0]
      : "/deals/deal_salon_cosmetics.jpg";

  // Safely extract string category name to prevent rendering object as React child
  const category = toSafeString(
    offer.category ||
    partner?.businessDetails?.businessType ||
    partner?.serviceCategories?.[0],
    "Special Offer"
  );

  const badgeText = toSafeString(
    offer.badge ||
    (offer.discountType === "bogo"
      ? "BUY 1 GET 1"
      : offer.discountValue
      ? `${offer.discountValue}% OFF`
      : "SPECIAL DEAL"),
    "SPECIAL DEAL"
  );

  const expiryText = offer.validTo || offer.validUntil
    ? new Date(offer.validTo || offer.validUntil!).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  const rawTerms =
    offer.termsAndConditions && offer.termsAndConditions.length > 0
      ? offer.termsAndConditions
      : offer.terms && offer.terms.length > 0
      ? offer.terms
      : [
          "Valid for all SetGo users.",
          "Show this offer at the counter before billing.",
          "Cannot be combined with any other in-store promotion.",
          "Subject to partner store opening hours and availability.",
        ];

  const terms = rawTerms.map((term: any) => toSafeString(term)).filter(Boolean);

  const relatedOffers = offer.relatedOffers || [];

  return (
    <div className="min-h-screen bg-[#F6F7FA] text-gray-900 font-sans selection:bg-purple-100 selection:text-purple-900">
      <SEO
        title={`${offer.title} · SetGo Deals`}
        description={offer.description || `Redeem ${offer.title} from ${merchantName} on SetGo.`}
        image={coverImage}
        url={`/offers/${offer._id}`}
      />

      <Header variant="solid" />

      <main className="pt-20 sm:pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb Navigation */}
          <nav aria-label="Breadcrumb" className="mb-4">
            <ol className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500 font-medium">
              <li>
                <Link to="/deals" className="hover:text-purple-700 transition-colors">
                  Deals near you
                </Link>
              </li>
              <li>
                <span className="text-gray-300">/</span>
              </li>
              <li>
                <Link
                  to={`/offers?category=${encodeURIComponent(category)}`}
                  className="hover:text-purple-700 transition-colors"
                >
                  {category}
                </Link>
              </li>
              <li>
                <span className="text-gray-300">/</span>
              </li>
              <li className="text-gray-900 font-bold truncate max-w-[200px] sm:max-w-none">
                {offer.title}
              </li>
            </ol>
          </nav>

          {/* ── TOP SECTION: OFFER HERO & CLAIM CARD ──────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch mb-8">
            {/* Left Column: Big Cover Image with floating badges */}
            <div className="lg:col-span-7 relative rounded-3xl overflow-hidden shadow-md bg-gray-900 aspect-[16/11] sm:aspect-[16/10] lg:aspect-auto lg:min-h-[440px]">
              <img
                src={coverImage}
                alt={offer.title}
                className="w-full h-full object-cover object-center"
              />
              {/* Top-Left Category Pill */}
              <div className="absolute top-4 left-4 sm:top-5 sm:left-5">
                <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/95 text-gray-900 backdrop-blur-md shadow-md border border-white/40">
                  {category}
                </span>
              </div>

              {/* Bottom-Left Discount Badge */}
              <div className="absolute bottom-4 left-4 sm:bottom-5 sm:left-5">
                <span className="inline-block px-4 py-1.5 rounded-xl text-xs sm:text-sm font-extrabold uppercase tracking-wider bg-[#108A4E] text-white shadow-lg">
                  {badgeText}
                </span>
              </div>
            </div>

            {/* Right Column: Merchant Info + Claim Card */}
            <div className="lg:col-span-5 flex flex-col justify-between gap-4">
              {/* Top Merchant Details Card */}
              <div className="bg-white rounded-3xl p-6 sm:p-7 shadow-sm border border-gray-100 flex-1 flex flex-col justify-between">
                <div>
                  {/* Merchant Header */}
                  <Link
                    to={`/shops/${merchantSlug}`}
                    className="flex items-center gap-3.5 group"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-[#FFF9ED] border border-amber-200/80 p-1 flex items-center justify-center overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform shadow-xs">
                      {merchantLogo ? (
                        <img
                          src={merchantLogo}
                          alt={merchantName}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <span className="text-amber-500 font-serif italic font-extrabold text-2xl leading-none">
                          {merchantName.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>

                    <div className="min-w-0">
                      <h2 className="font-extrabold text-base text-gray-900 group-hover:text-purple-700 transition-colors truncate">
                        {merchantName}
                      </h2>
                      <p className="text-xs text-gray-500 flex items-center gap-1 truncate mt-0.5">
                        <svg
                          className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="truncate">
                          {merchantAddress || "Partner store"}{distance ? ` · ${distance}` : ""}
                        </span>
                      </p>
                    </div>
                  </Link>

                  {/* Title & Subtitle */}
                  <h1 className="text-2xl sm:text-3xl font-black text-gray-900 mt-5 leading-tight tracking-tight">
                    {offer.title}
                  </h1>

                  <p className="text-sm text-gray-600 mt-2 leading-relaxed font-normal">
                    {offer.description || "Get exclusive savings on your visit."}
                  </p>
                </div>

                {/* Expiry & Perk Badges */}
                <div className="flex flex-wrap items-center gap-2 pt-5 border-t border-gray-100 mt-5">
                  {expiryText ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#FEF6E7] text-[#925C0C] border border-[#FDE6BA]">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      Expires {expiryText}
                    </span>
                  ) : null}

                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#EFEBFA] text-[#552FB5] border border-purple-100">
                    Earn coins on redemption
                  </span>
                </div>
              </div>

              {/* Bottom Claim Card (Dark Indigo with Badges) */}
              <div className="bg-[#18123A] rounded-3xl p-5 sm:p-6 text-white shadow-xl relative overflow-hidden">
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl pointer-events-none" />

                <div className="text-[10px] font-black uppercase tracking-wider text-[#FFC727] mb-1">
                  CLAIM THIS DEAL
                </div>

                <h3 className="text-base sm:text-lg font-extrabold leading-snug tracking-tight mb-4">
                  Book or redeem it in the SetGo app
                </h3>

                {/* Store download badges */}
                <div className="flex gap-2.5 sm:gap-3">
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-all group"
                  >
                    <svg className="w-5 h-5 fill-current flex-shrink-0" viewBox="0 0 24 24">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8 0.92-2.87-.91.04-2.02.61-2.67 1.37-.58.67-1.09 1.74-.96 2.78 1.02.08 2.08-.53 2.71-1.28" />
                    </svg>
                    <div className="text-left leading-tight min-w-0">
                      <div className="text-[9px] uppercase tracking-wider text-gray-300 font-medium">Download on the</div>
                      <div className="text-xs sm:text-sm font-bold truncate">App Store</div>
                    </div>
                  </a>

                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-all group"
                  >
                    <svg className="w-4 h-4 fill-current flex-shrink-0" viewBox="0 0 24 24">
                      <path d="M3.609 1.814L13.792 12 3.61 22.186c-.342-.32-.553-.787-.553-1.309V3.123c0-.522.211-.989.552-1.309zm11.238 11.24l2.253 2.254-11.83 6.828 9.577-9.082zm0-2.108L5.27 1.864l11.83 6.828-2.253 2.254zm1.493 1.054l3.056 1.764c.895.517.895 1.365 0 1.882l-3.056 1.764-1.854-1.705 1.854-1.705z" />
                    </svg>
                    <div className="text-left leading-tight min-w-0">
                      <div className="text-[9px] uppercase tracking-wider text-gray-300 font-medium">Get it on</div>
                      <div className="text-xs sm:text-sm font-bold truncate">Google Play</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* ── DETAILS & TERMS CARD ─────────────────────────────────────────── */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100 mb-10">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Details & terms
            </h3>

            <div className="space-y-3 text-xs sm:text-sm text-gray-600">
              {terms.map((term, idx) => (
                <div key={idx} className="flex items-start gap-2.5">
                  <span className="text-[#108A4E] font-bold text-base leading-none mt-0.5">
                    ✓
                  </span>
                  <span className="leading-relaxed">{term}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── MORE FROM MERCHANT SECTION ───────────────────────────────────── */}
          {relatedOffers.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                  More from {merchantName}
                </h3>
                <Link
                  to={`/shops/${merchantSlug}`}
                  className="text-xs sm:text-sm font-semibold text-[#108A4E] hover:underline flex items-center gap-1"
                >
                  View shop →
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                {relatedOffers.map((ro, idx) => {
                  const roImg =
                    ro.images && ro.images.length > 0
                      ? ro.images[0]
                      : "/deals/gallery_interior.jpg";

                  const isThirdCard = idx === 2;

                  return (
                    <Link
                      key={ro._id}
                      to={`/offers/${ro._id}`}
                      className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between ${
                        isThirdCard
                          ? "border-2 border-[#1E90FF]"
                          : "border border-gray-100"
                      }`}
                    >
                      <div className="relative aspect-[16/10] w-full overflow-hidden bg-gray-100">
                        <img
                          src={roImg}
                          alt={ro.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="inline-block px-3 py-1 rounded-md text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-[#108A4E] text-white shadow-sm">
                            {ro.badge || "SPECIAL DEAL"}
                          </span>
                        </div>
                      </div>

                      <div className="p-4 sm:p-5">
                        <h4 className="font-extrabold text-sm sm:text-base text-gray-900 group-hover:text-purple-700 transition-colors line-clamp-1">
                          {ro.title}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                          {ro.description || "Exclusive in-store offer"}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* App Download Banner */}
      <AppDownloadBanner />

      {/* Global Footer */}
      <Footer />
    </div>
  );
}
